import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Clock, ArrowRight, Briefcase, Zap, Heart, Users, Globe, ChevronDown, ChevronUp } from 'lucide-react';

const CareersPage = () => {
  const [openJob, setOpenJob] = useState(null);
  const [selectedDept, setSelectedDept] = useState('All');

  const departments = ['All', 'Engineering', 'Product', 'Operations', 'Sales', 'Marketing'];

  const perks = [
    // Honest for a pre-launch, two-founder company. The previous list promised
    // a London office, private healthcare, a lunch stipend and benchmarked
    // salaries — none of which exist. Replaced 2026-10-13.
    { icon: '🏡', title: 'Remote First', desc: 'We build from home. Our registered office is in London; the work happens wherever you are.' },
    { icon: '🛠️', title: 'Real Ownership', desc: 'A small team means your work ships and you see it land. Nothing you build disappears into a backlog.' },
    { icon: '🌍', title: 'Build for a Real Problem', desc: 'We were short-let hosts first. Everything we build is something we needed ourselves.' },
    { icon: '💬', title: 'Direct Access', desc: 'You work with the founders, not through layers. Decisions get made the same day.' },
  ];

  // No invented vacancies. Ariv is a two-founder, pre-launch company; the page
  // renders its own "no open roles" empty state and the speculative-application
  // route below. Removed 2026-10-13 with the fabricated perks and headline
  // stats (a "team of 65", 12 roles and 3 offices that never existed).
  const jobs = [];

  const filteredJobs = selectedDept === 'All' ? jobs : jobs.filter(j => j.dept === selectedDept);

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/"><div className="h-[30px] w-auto flex items-center justify-center flex-shrink-0"><img src="/logo.png" alt="Ariv" className="h-[30px] w-auto object-contain" /></div></Link>
          <div className="flex items-center gap-6">
            <Link to="/about" className="text-gray-600 hover:text-[#1F3A68] text-sm font-medium">About</Link>
            <Link to="/contact" className="text-gray-600 hover:text-[#1F3A68] text-sm font-medium">Contact</Link>
            <Link to="/login" className="bg-[#1F3A68] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#1F3A68]/90">Sign In</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-[#1F3A68] text-white py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 text-sm font-medium mb-6">
              <Zap size={14} className="text-yellow-400" />
              Work with us
            </div>
            <h1 className="text-5xl font-bold mb-5 leading-tight">Help us build the future of UK property hosting</h1>
            <p className="text-xl text-white/80 mb-8">
              We're a small founding team on a mission to professionalise short-term rentals. We're early, and we'd rather hear from the right people now than advertise roles later.
            </p>
            <div className="flex gap-4 flex-wrap">
              <div className="bg-white/10 rounded-xl px-5 py-3 text-center">
                <div className="text-2xl font-bold">2</div>
                <div className="text-sm text-white/70">Founders</div>
              </div>
              <div className="bg-white/10 rounded-xl px-5 py-3 text-center">
                <div className="text-2xl font-bold">London</div>
                <div className="text-sm text-white/70">Registered office</div>
              </div>
              <div className="bg-white/10 rounded-xl px-5 py-3 text-center">
                <div className="text-2xl font-bold">Remote</div>
                <div className="text-sm text-white/70">We build from home</div>
              </div>
              <div className="bg-white/10 rounded-xl px-5 py-3 text-center">
                <div className="text-2xl font-bold">Pre-launch</div>
                <div className="text-sm text-white/70">Stage</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Perks */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-[#CB3B3B] font-semibold text-sm mb-3">
              <Heart size={16} />
              WHY ARIV
            </div>
            <h2 className="text-4xl font-bold text-[#1F3A68]">Life at Ariv</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {perks.map((p, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="text-3xl mb-4">{p.icon}</div>
                <h3 className="font-bold text-[#1F3A68] mb-2">{p.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Jobs */}
      <section className="py-20 max-w-7xl mx-auto px-6">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 text-[#CB3B3B] font-semibold text-sm mb-3">
            <Briefcase size={16} />
            OPEN ROLES
          </div>
          <h2 className="text-4xl font-bold text-[#1F3A68] mb-6">Open roles</h2>
          {/* Department filter */}
          <div className="flex flex-wrap justify-center gap-2">
            {departments.map(d => (
              <button
                key={d}
                onClick={() => setSelectedDept(d)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedDept === d
                    ? 'bg-[#1F3A68] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          {filteredJobs.map(job => (
            <div key={job.id} className="border border-gray-200 rounded-2xl overflow-hidden hover:border-[#1F3A68]/30 transition-colors">
              <button
                onClick={() => setOpenJob(openJob === job.id ? null : job.id)}
                className="w-full px-8 py-6 flex items-center justify-between text-left"
              >
                <div className="flex items-start gap-4">
                  <div>
                    <h3 className="text-lg font-bold text-[#1F3A68]">{job.title}</h3>
                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                      <span className="flex items-center gap-1.5"><MapPin size={13} />{job.location}</span>
                      <span className="flex items-center gap-1.5"><Clock size={13} />{job.type}</span>
                      <span className="bg-[#1F3A68]/10 text-[#1F3A68] px-2.5 py-0.5 rounded-full text-xs font-medium">{job.dept}</span>
                    </div>
                  </div>
                </div>
                {openJob === job.id ? <ChevronUp size={20} className="text-gray-400 flex-shrink-0" /> : <ChevronDown size={20} className="text-gray-400 flex-shrink-0" />}
              </button>

              {openJob === job.id && (
                <div className="px-8 pb-8 border-t border-gray-100 pt-6">
                  <p className="text-gray-700 mb-6">{job.description}</p>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-bold text-[#1F3A68] mb-3">What you'll need</h4>
                      <ul className="space-y-2">
                        {job.requirements.map((r, i) => (
                          <li key={i} className="flex items-start gap-2 text-gray-600 text-sm">
                            <span className="text-[#1F3A68] mt-0.5">•</span>{r}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#1F3A68] mb-3">Nice to have</h4>
                      <ul className="space-y-2">
                        {job.niceToHave.map((n, i) => (
                          <li key={i} className="flex items-start gap-2 text-gray-600 text-sm">
                            <span className="text-green-500 mt-0.5">✓</span>{n}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <Link
                    to="/contact"
                    className="mt-8 inline-flex items-center gap-2 bg-[#1F3A68] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#1F3A68]/90 transition-colors"
                  >
                    Apply for this role
                    <ArrowRight size={16} />
                  </Link>
                </div>
              )}
            </div>
          ))}
        </div>

        {filteredJobs.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <Briefcase size={48} className="mx-auto mb-4 opacity-40" />
            <p>We're not advertising roles yet — we're a two-founder team getting Ariv to launch. If you want to be first in line when we do hire, send us a speculative application below.</p>
          </div>
        )}
      </section>

      {/* Speculative */}
      <section className="bg-[#1F3A68] text-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Globe size={40} className="mx-auto mb-4 opacity-60" />
          <h2 className="text-3xl font-bold mb-3">Don't see the right role?</h2>
          <p className="text-white/70 mb-8 text-lg">We're always interested in exceptional people. Send us your CV and tell us how you'd contribute.</p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-[#1F3A68] px-8 py-4 rounded-xl font-bold hover:bg-gray-50 transition-colors"
          >
            Send a speculative application
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1F3A68] text-white py-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="h-[23px] w-auto flex items-center justify-center flex-shrink-0"><img src="/logo.png" alt="Ariv" className="h-[23px] w-auto object-contain brightness-0 invert" /></div>
          <div className="flex gap-6 text-sm text-white/70">
            <Link to="/about" className="hover:text-white">About</Link>
            <Link to="/careers" className="hover:text-white">Careers</Link>
            <Link to="/contact" className="hover:text-white">Contact</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
          </div>
          <p className="text-white/50 text-sm">&copy; {new Date().getFullYear()} Ariv</p>
        </div>
      </footer>
    </div>
  );
};

export default CareersPage;
